import React, { useRef } from "react";

import { Loader } from "../Loader";
import { WidgetError } from "../WidgetError";

import { ChartContainer } from "./ChartContext";
import ChartDescription from "./ChartDescription";
import { StatusBarItemType, Tooltip, useResizeObserver } from "./helpers";
import StackedBar from "./StackedBar";
import styles from "./StatusBarChart.scss";

/**
 * Props for the StatusBarChart component.
 */
export interface StatusBarChartProps {
  /** Number of "On track" goals (green bar). */
  onTrackGoals?: number;
  /** Number of "Behind" goals (yellow/orange bar). */
  behindGoals?: number;
  /** Number of "At risk" goals (red bar). */
  atRiskGoals?: number;
  /** URL for the details link in the chart description. */
  linkUrl?: string;
  /** Label for the details link in the chart description. */
  linkLabel?: string;
  /** If true, shows an error state instead of the chart. */
  error?: boolean;
  /** If true, shows a loading spinner instead of the chart. */
  loading?: boolean;
  /** Custom title text for the chart description. */
  statusTitle?: string;
  /** Custom description text for the chart description. */
  statusDescription?: string;
  /** If false, hides the title and description below the bar. */
  showTitleAndDescription?: boolean;
  /**
   * Custom tooltip text for each status type.
   * Keys are StatusBarItemType values: "on_track", "behind", "at_risk".
   * Each value is an object with optional `status` and `text` fields.
   */
  tooltips?: Record<StatusBarItemType, { status?: string; text?: string }>;
  /** Sets the height of the bar (in px). */
  newBarHeight?: number;
  /** Sets the top position of the bar (in px). */
  newTop?: number;
}

export function StatusBarChart({
  onTrackGoals = 0,
  behindGoals = 0,
  atRiskGoals = 0,
  linkUrl = "",
  linkLabel = "View details",
  error = false,
  loading = false,
  statusTitle = undefined,
  statusDescription = undefined,
  showTitleAndDescription = true,
  tooltips = undefined,
  newBarHeight = 23,
  newTop = 4,
}: StatusBarChartProps) {
  const data = [
    {
      points: onTrackGoals,
      tag: StatusBarItemType.ON_TRACK,
    },
    {
      points: behindGoals,
      tag: StatusBarItemType.BEHIND,
    },
    {
      points: atRiskGoals,
      tag: StatusBarItemType.AT_RISK,
    },
  ];

  const containerRef = useRef<HTMLDivElement>(null);
  const dimensions = useResizeObserver(containerRef);

  const tooltipContent = Tooltip;
  const ctxValue = {
    data,
    dimensions,
    linkLabel,
    linkUrl,
    newBarHeight,
    newTop,
    statusTitle,
    statusDescription,
    tooltips,
    tooltipContent,
  };

  if (loading) {
    return (
      <div data-testid="chart-loader">
        <Loader centered />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center" }}
        data-testid="chart-error"
      >
        <WidgetError text="There was an issue loading this chart" />
      </div>
    );
  }

  return (
    <div className={styles["stacked-bar-chart"]} ref={containerRef}>
      <ChartContainer state={ctxValue}>
        {showTitleAndDescription && <ChartDescription />}
        <StackedBar />
      </ChartContainer>
    </div>
  );
}
